export type VersionSource = 'native' | 'json' | 'none';

type NativeAppInfo = {
  version?: unknown;
  build?: unknown;
};

const FALLBACK_VERSION = '1.0.0';
const SOURCE_TIMEOUT_MS = 3_000;

export class VersionLoader
{
  private static _version = FALLBACK_VERSION;
  private static _build = 0;
  private static _source: VersionSource = 'none';
  private static load_promise: Promise<string> | null = null;
  private static settled = false;

  static load(): Promise<string>
  {
    if ( !this.load_promise )
      this.load_promise = this.loadInner();

    return this.load_promise;
  }

  static getVersion(): string { return this._version; }

  public static getBuildNumber(): number { return this._build; }

  public static isBuildKnown(): boolean { return this._build > 0; }

  public static isSettled(): boolean { return this.settled; }

  public static getSource(): VersionSource { return this._source; }

  private static async loadInner(): Promise<string>
  {
    const platform = window.Capacitor?.getPlatform?.() || 'web';

    try
    {
      if ( platform === 'android' || platform === 'ios' )
      {
        const native = await this.readNative();
        if ( native )
        {
          this.apply( native.version, native.build, 'native' );
          return this._version;
        }
      }

      const json = await this.readVersionJson( platform );
      if ( json )
      {
        this.apply( json.version, json.build, 'json' );
        return this._version;
      }

      console.warn( `[VersionLoader] version unknown, using fallback [${FALLBACK_VERSION}]` );
      return this._version;
    }
    finally
    {
      this.settled = true;
      console.info( `[VersionLoader] version=${this._version} build=${this._build} source=${this._source}` );
    }
  }

  private static apply( version: string, build: number, source: VersionSource ): void
  {
    this._build = build;
    this._source = source;

    const parts = version.split( '.' );
    if ( parts.length >= 3 && parseInt( parts[parts.length - 1] ) === build )
    {
      this._version = version;
      return;
    }

    const major = parts[0] || '0';
    const minor = parts[1] || '0';
    this._version = `${major}.${minor}.${build}`;
  }

  private static async readNative(): Promise<{ version: string; build: number } | null>
  {
    const app = ( window as any )?.Capacitor?.Plugins?.App;
    if ( !app || typeof app.getInfo !== 'function' )
      return null;

    try
    {
      const info = await this.withTimeout<NativeAppInfo>( app.getInfo(), 'native App.getInfo' );
      const build = this.toBuild( info?.build );
      if ( build <= 0 )
        return null;

      const version = typeof info?.version === 'string' && info.version.trim()
        ? info.version.trim()
        : `0.0.${build}`;

      return { version, build };
    }
    catch ( err )
    {
      console.warn( '[VersionLoader] native app info unavailable', err );
      return null;
    }
  }

  private static async readVersionJson( platform: string ): Promise<{ version: string; build: number } | null>
  {
    try
    {
      const random_seed = Math.floor( Math.random() * 1e8 );
      const controller = new AbortController();
      const timer = setTimeout( () => controller.abort(), SOURCE_TIMEOUT_MS );
      let json: any;
      try
      {
        const response = await fetch( `version.json?v=${random_seed}`, { signal: controller.signal } );
        if ( !response.ok )
          throw new Error( `HTTP ${response.status}` );
        json = await response.json();
      }
      finally
      {
        clearTimeout( timer );
      }

      const major = String( json?.major ?? '1' );
      const minor = String( json?.minor ?? '0' );
      const section = platform === 'android' ? json?.android
        : platform === 'ios' ? json?.ios
        : json?.web;
      const build = this.toBuild( section?.build );
      if ( build <= 0 )
        return null;

      return { version: `${major}.${minor}.${build}`, build };
    }
    catch ( err )
    {
      console.warn( '[VersionLoader] failed to load version.json', err );
      return null;
    }
  }

  private static toBuild( value: unknown ): number
  {
    const n = typeof value === 'number' ? value : parseInt( String( value ?? '' ), 10 );
    return Number.isFinite( n ) && n > 0 ? Math.floor( n ) : 0;
  }

  private static withTimeout<T>( promise: Promise<T>, label: string ): Promise<T>
  {
    return new Promise<T>( ( resolve, reject ) =>
    {
      const timer = setTimeout( () => reject( new Error( `${label} timed out` ) ), SOURCE_TIMEOUT_MS );
      promise.then(
        value => { clearTimeout( timer ); resolve( value ); },
        err => { clearTimeout( timer ); reject( err ); },
      );
    } );
  }
}
